import { useEffect, useState } from 'react';
import axios from 'axios';

// Mismo origen: nginx hace proxy de /api/ hacia Flask.
export const apiClient = axios.create({ baseURL: '' });

// Pedidos en curso: con eso la pantalla avisa que está trabajando (ver useApiBusy). Los pedidos con
// `silent: true` no cuentan: son los que se repiten solos, como el avance de la sincronización.
let pending = 0;
const busyListeners = new Set();
const track = (config, delta) => {
    if (config?.silent) return;
    pending = Math.max(0, pending + delta);
    busyListeners.forEach(listener => listener(pending > 0));
};

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
        track(config, 1);
        return config;
    },
    (error) => Promise.reject(error)
);

// Quien quiere enterarse de que la sesión venció (la app, para volver al login). Devuelve cómo dejar de escuchar.
const expiredListeners = new Set();
export const onSessionExpired = (listener) => { expiredListeners.add(listener); return () => { expiredListeners.delete(listener); }; };

apiClient.interceptors.response.use(
    (response) => { track(response.config, -1); return response; },
    (error) => {
        track(error.config, -1);
        // Con token, un 401 es sesión vencida (a las 8 horas) o usuario dado de baja: reintentar con el mismo token no sirve.
        // Sin token es un login con datos mal puestos, que se resuelve en su pantalla.
        if (error.response?.status === 401 && localStorage.getItem('accessToken')) expiredListeners.forEach(listener => listener());
        return Promise.reject(error);
    }
);

// `true` mientras haya algún pedido al servidor sin terminar.
export const useApiBusy = () => {
    const [busy, setBusy] = useState(pending > 0);
    useEffect(() => {
        busyListeners.add(setBusy);
        setBusy(pending > 0);
        return () => { busyListeners.delete(setBusy); };
    }, []);
    return busy;
};

// El backend manda el mensaje de error en `msg` y en `error`.
export const errorMessage = (e, fallback = 'Error en la operación.') => e.response?.data?.msg || e.response?.data?.error || fallback;

// Las descargas necesitan el token, así que no alcanza con un <a href>: se baja como blob y se dispara a mano.
// Devuelve la respuesta, por si quien llama quiere leer lo que el servidor dice del archivo en los encabezados.
export const downloadFile = async (url, filename, params = {}) => {
    const response = await apiClient.get(url, { params, responseType: 'blob' });
    const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = objectUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
    return response;
};
